import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  PremiseRentApiPayload,
  PremiseRentResponse,
  PremiseRentContract,
  PremiseRentListResponse,
} from "../types";

export const premiseRentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitPremiseRent: builder.mutation<PremiseRentResponse, PremiseRentApiPayload>({
      query: (body) => ({
        url: "/api/legal/premises-leases/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PremisesLeases" as const, id: "LIST" }],
    }),

    updatePremiseRent: builder.mutation<PremiseRentResponse, { id: number; data: PremiseRentApiPayload }>({
      query: ({ id, data }) => ({
        url: `/api/legal/premises-leases/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PremisesLeases" as const, id },
        { type: "PremisesLeases" as const, id: "LIST" },
      ],
    }),

    getPremiseRent: builder.query<PremiseRentResponse, number>({
      query: (id) => ({
        url: `/api/legal/premises-leases/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "PremisesLeases" as const, id }],
    }),

    getPremiseRentList: builder.query<PremiseRentContract[], void>({
      query: () => ({
        url: "/api/legal/premises-leases/",
        method: "GET",
      }),
      transformResponse: (response: PremiseRentListResponse): PremiseRentContract[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PremisesLeases" as const, id })),
              { type: "PremisesLeases" as const, id: "LIST" },
            ]
          : [{ type: "PremisesLeases" as const, id: "LIST" }],
    }),

    submitPremiseRentForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/premises-leases/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "PremisesLeases" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          premiseRentApi.util.updateQueryData("getPremiseRent", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadPremiseRentPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/premises-leases/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    deletePremiseRent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/premises-leases/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PremisesLeases" as const, id },
        { type: "PremisesLeases" as const, id: "LIST" },
      ],
    }),

    revokePremiseRent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/premises-leases/${id}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PremisesLeases" as const, id },
        { type: "PremisesLeases" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
  }),
});

export const {
  useSubmitPremiseRentMutation,
  useUpdatePremiseRentMutation,
  useGetPremiseRentQuery,
  useGetPremiseRentListQuery,
  useSubmitPremiseRentForSigningMutation,
  useDownloadPremiseRentPreviewMutation,
  useDeletePremiseRentMutation,
  useRevokePremiseRentMutation,
} = premiseRentApi;
